/* eslint-disable no-unused-vars */
import { memo, useState, useRef, useEffect, useId } from 'react';
import { useSelector } from 'react-redux';
import { Each } from 'components';
import { LeftSideHeader } from './leftSideHeader/LeftSideHeader';
import { useComputed } from '@preact/signals-react';
import { RightSideHeader } from './rightSideHeader/RightSideHeader';
import { RightSideRow } from './rightSideRow/RightSideRow';
import { LeftSideRow } from './leftSideRow/LeftSideRow';
import { NoDataToDisplay } from './NoDataToDisplay';
import * as S from './TableBody.styled';

const onHoverAction = ({ rowId, index, background, isShowBid, isChecked }) => {
  const targetClassName = `${rowId}_${index}`;
  const elements = Array.from(document.querySelectorAll(`[class*="${rowId}_"]`));
  const targetElements = elements.filter((element) => element.classList.contains(targetClassName));
  targetElements.forEach((element) => {
    if (isChecked) {
      element.style.setProperty('background-color', background, 'important');
    } else {
      element.style.backgroundColor = background;
    }
    const changeBidBox = element.querySelector('[class*="change_bid_trigger_box"]');
    if (changeBidBox && isShowBid) {
      changeBidBox.style.setProperty('visibility', 'visible');
    }
    if (changeBidBox && !isShowBid) {
      changeBidBox.style.setProperty('visibility', 'hidden');
    }
  });
};

export const TableBody = memo(
  ({ filteredData$, sortConfig$, isUa, originalTableData$, visibleRows, isNoData$ }) => {
    const selectedEventsLength = useSelector(
      (state) => state.campaignReports.selectedEvents?.filter((item) => item.isSelected)?.length,
    );
    const selectedTableItems = useSelector((state) => state.campaignReports.selectedTableItems);
    const currentRequestType = useSelector((state) => state.campaignReports.tableRequestTypes);
    const selectedCols = useSelector((state) => state.campaignReports.selectedCols);

    const DynamicLeftComponent = LeftSideRow[currentRequestType];
    const rowId = useId();

    const scrollBody = useRef(null);
    const scrollHeader = useRef(null);
    const scrollBar = useRef(null);
    const scrollBarInner = useRef(null);

    const leftSideContainer = useRef(null);
    const rightSideHeaderContainer = useRef(null);

    const [isScroll, setIsScroll] = useState(false);

    function updateScrollBar() {
      if (rightSideHeaderContainer.current && scrollBarInner.current) {
        scrollBarInner.current.style.minWidth =
          rightSideHeaderContainer.current.children[0]?.offsetWidth + 'px';
      }
      if (!hasHorizontalScroll(scrollBar.current)) {
        setIsScroll(false);
        scrollBar.current.style.pointerEvents = 'none';
      } else {
        setIsScroll(true);
        scrollBar.current.style.pointerEvents = 'auto';
      }
    }

    useEffect(() => {
      const timer = setTimeout(() => {
        updateScrollBar();
        window.addEventListener('resize', updateScrollBar);
      }, 0);

      if (!filteredData$.value?.length) {
        toggleShadow(0);
      }

      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', updateScrollBar);
      };
    }, [selectedEventsLength, filteredData$.value?.length, selectedCols]);

    const handleScrollBar = (event) => {
      if (!scrollBody.current || !scrollHeader.current) return;
      const scrollLeft = event.target.scrollLeft;
      scrollBody.current.scrollLeft = event.target.scrollLeft;
      scrollHeader.current.scrollLeft = event.target.scrollLeft;
      toggleShadow(scrollLeft);
    };

    const handleScrollBody = (event) => {
      if (!scrollHeader.current || !scrollBar.current) return;
      const scrollLeft = event.target.scrollLeft;
      scrollHeader.current.scrollLeft = scrollLeft;
      scrollBar.current.scrollLeft = scrollLeft;
      toggleShadow(scrollLeft);
    };

    const handleScrollHeader = (event) => {
      if (!scrollBody.current || !scrollBar.current) return;
      const scrollLeft = event.target.scrollLeft;

      scrollBody.current.scrollLeft = scrollLeft;
      scrollBar.current.scrollLeft = scrollLeft;
      toggleShadow(scrollLeft);
    };

    const toggleShadow = (curScroll) => {
      if (curScroll > 0) {
        leftSideContainer.current.classList.add('shadow');
      } else {
        leftSideContainer.current.classList.remove('shadow');
      }
    };

    const onMouseEnterRow = (index, isChecked) => {
      onHoverAction({ rowId, index, background: 'rgb(241, 245, 249)', isShowBid: true, isChecked });
    };
    const onMouseLeaveRow = (index) => {
      onHoverAction({ rowId, index, background: '', isShowBid: false, isChecked: false });
    };

    return (
      <>
        <S.TableBody>
          <S.LeftSide ref={leftSideContainer}>
            <S.Header style={sticky} isNoData={isNoData$.value}>
              <LeftSideHeader
                originalTableData$={originalTableData$}
                filteredData$={filteredData$}
                isNoData$={isNoData$}
                sortConfig$={sortConfig$}
              />
            </S.Header>
            {!isNoData$.value ? (
              <S.RowsWrap>
                <Each
                  of={filteredData$.value?.slice(0, visibleRows)}
                  render={(item, index) => {
                    const isChecked = selectedTableItems.some((el) => el.pba_id === item.pba_id);
                    return (
                      <S.Row
                        className={`${rowId}_${index}`}
                        onMouseEnter={() => onMouseEnterRow(index, isChecked)}
                        onMouseLeave={() => onMouseLeaveRow(index)}
                        isScroll={isScroll}
                        isChecked={isChecked}
                      >
                        <DynamicLeftComponent item={item} isCheckbox={true} />
                      </S.Row>
                    );
                  }}
                />
              </S.RowsWrap>
            ) : null}
          </S.LeftSide>
          <S.RightSide>
            <S.ScrollWrap ref={scrollHeader} style={sticky} onScroll={handleScrollHeader}>
              <S.Header ref={rightSideHeaderContainer} isNoData={isNoData$.value}>
                <RightSideHeader
                  data$={filteredData$}
                  sortConfig$={sortConfig$}
                  isUa={isUa}
                  isNoData$={isNoData$}
                />
              </S.Header>
            </S.ScrollWrap>
            {!isNoData$.value ? (
              <S.ScrollWrap ref={scrollBody} onScroll={handleScrollBody}>
                <S.RowsWrap>
                  <Each
                    of={filteredData$.value?.slice(0, visibleRows)}
                    render={(item, index) => {
                      const isChecked = selectedTableItems.some((el) => el.pba_id === item.pba_id);
                      return (
                        <S.Row
                          className={`${rowId}_${index}`}
                          onMouseEnter={() => onMouseEnterRow(index, isChecked)}
                          onMouseLeave={() => onMouseLeaveRow(index)}
                          isChecked={isChecked}
                          key={item.pba_number}
                          isScroll={isScroll}
                        >
                          <RightSideRow data={item} isUa={isUa} />
                        </S.Row>
                      );
                    }}
                  />
                </S.RowsWrap>
              </S.ScrollWrap>
            ) : null}
          </S.RightSide>
          <style>{stylesOverride}</style>
        </S.TableBody>
        <S.ScrollBar>
          <S.RightScrollBlock onScroll={handleScrollBar} ref={scrollBar}>
            <S.ScrollInner ref={scrollBarInner} />
          </S.RightScrollBlock>
        </S.ScrollBar>
        {isNoData$.value && <NoDataToDisplay />}
      </>
    );
  },
);

function hasHorizontalScroll(element) {
  return element.scrollWidth > element.clientWidth;
}

const stylesOverride = `
  html, body, .App, #root   {
    height: unset !important;
  }
`;

const sticky = {
  position: 'sticky',
  top: '76px',
};

TableBody.displayName = 'TableBody';
